import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import Calendar from "react-calendar";
import moment from "moment";
import Label from "~/components-shared/Label";
import Dropdown from "~/components-shared/Dropdown";
import Icon from "~/components-shared/Icon";
import InputText from "~/components-shared/InputText";
const Wrapper = styled.div`
  ${_ref => {
  let {
    $s,
    theme
  } = _ref;
  return theme.spacing.resolve(theme, $s);
}};
  ${_ref2 => {
  let {
    $fullWidth
  } = _ref2;
  return $fullWidth && "width: 100%";
}};
  ${_ref3 => {
  let {
    $maxWidth
  } = _ref3;
  return $maxWidth && `max-width: ${$maxWidth}`;
}};
`;
const Field = styled.div`
  width: 100%;
  border: none;
  border-radius: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.shape.borderRadius.primary;
}};
  font-weight: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.typography.fontWeight.regular;
}};
  display: flex;
  align-items: center;
  transition: box-shadow 200ms ease-in;
  box-shadow: rgb(255 255 255) 0 0 0 0, transparent 0 0 0 1px;
  ${_ref6 => {
  let {
    $outline,
    $hasError,
    theme
  } = _ref6;
  return theme.shadows.outline(theme, $outline, $hasError ? theme.colors.danger.main : theme.colors.primary.main);
}}
`;
const StyledCalendar = styled(Calendar)`
  padding: 1em;

  .react-calendar__navigation {
    display: flex;
    width: 100%;
    height: 35px;
  }
  .react-calendar__navigation__arrow {
    width: 30px;

    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
    }
  }
  .react-calendar__navigation__arrow,
  .react-calendar__navigation__label {
    background: transparent;
    border: none;
  }

  .react-calendar__month-view__weekdays__weekday {
    text-align: center;

    & > abbr {
      text-decoration: none;
      color: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.colors.text.secondary;
}};
    }
  }

  .react-calendar__tile {
    background: transparent;
    border: none;
    height: 40px;
    position: relative;
    z-index: 1;
    cursor: pointer;

    &:after {
      content: "";
      position: absolute;
      z-index: -1;
      top: 50%;
      left: 50%;
      width: 80%;
      padding-top: 80%;
      background-color: transparent;
      border-radius: 50%;
      transform: translate(-50%, -50%);
    }
    
    &:hover:after {
      background-color: rgba(0, 0, 0, 0.1);
    }
    
    &--active {
      font-weight: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.typography.fontWeight.semiBold;
}};
      color: ${_ref9 => {
  let {
    theme
  } = _ref9;
  return theme.colors.background.primary;
}};
      
      &:after,
      &:hover:after {
        background-color: ${_ref10 => {
  let {
    theme
  } = _ref10;
  return theme.colors.primary.main;
}};
      }
  }
  
`;
const Error = styled.span`
  font-size: 0.8em;
  display: inline-block;
  margin-left: 0.5em;
  color: ${_ref11 => {
  let {
    theme
  } = _ref11;
  return theme.colors.danger.main;
}};
`;
const StyledInputText = styled(InputText)`
  padding-right: 0.1em;
`;
const StyledDropdownList = styled(Dropdown.List)`
  width: 300px;
`;
const StyledDropdown = styled(Dropdown)`
  ${_ref12 => {
  let {
    $hasError,
    theme
  } = _ref12;
  return $hasError && css`
      box-shadow: 0 0 0 2px ${theme.colors.danger.main};
    `;
}};
  border-radius: ${_ref13 => {
  let {
    theme
  } = _ref13;
  return theme.shape.borderRadius.primary;
}};
`;
const InputDate = _ref14 => {
  let {
    label,
    value = "",
    direction = "bottomRight",
    error,
    onChange = () => false,
    required = false,
    disabled = false,
    color = "translucent",
    maxWidth,
    fullWidth = false,
    s,
    outline,
    children,
    labelProps,
    ...rest
  } = _ref14;
  const theme = useTheme();
  const inputDateFormat = "ddd, D MMMM YYYY";
  const inputTimeFormat = "hh:mma";
  const dateTimeFormat = `${inputDateFormat} ${inputTimeFormat}`;
  const defaultValue = value === "" ? undefined : value;
  const defaultInput = value === "" ? "" : moment(value).format(inputDateFormat);
  const [inputValue, setInputValue] = useState(defaultInput);
  const defaultSearch = moment(moment(defaultValue).format(inputDateFormat)).toDate();
  const [searchValue, setSearchValue] = useState(defaultSearch);
  const [dateError, setDateError] = useState(false);
  useEffect(() => {
    setInputValue(defaultInput);
    setSearchValue(defaultSearch);
  }, [value]);
  const handleInput = v => {
    setInputValue(v);
    const isValid = moment(v).isValid();
    setDateError(!isValid);
    setSearchValue(moment(isValid ? v : defaultSearch).toDate());
  };
  const handleChange = v => {
    const formattedDate = moment(v).format(inputDateFormat);
    const start = moment(defaultValue);
    const remainder = 15 - start.minute() % 15;
    const formattedTime = start.add(remainder === 15 ? 0 : remainder, "minutes").format(inputTimeFormat);
    const date = moment(`${formattedDate} ${formattedTime}`, dateTimeFormat);
    if (date.isValid()) {
      setDateError(false);
      setInputValue(formattedDate);
      onChange(date.toISOString());
    } else {
      setDateError(true);
    }
  };
  const customHandler = (toggle, isOpen) => /*#__PURE__*/React.createElement(Field, {
    onClick: toggle,
    $outline: isOpen || outline,
    $hasError: dateError
  }, /*#__PURE__*/React.createElement(StyledInputText, _extends({
    value: inputValue,
    onChange: handleInput,
    color: color,
    autoComplete: "off",
    outline: false,
    disabled: disabled,
    elemAfter: /*#__PURE__*/React.createElement(Icon, {
      src: "chevron",
      scale: 5,
      color: theme.colors.text.secondary,
      rotate: isOpen ? 0 : 180
    })
  }, rest, {
    fullWidth: true
  })));
  return /*#__PURE__*/React.createElement(Wrapper, {
    $s: s,
    $maxWidth: maxWidth,
    $fullWidth: fullWidth
  }, !!label && /*#__PURE__*/React.createElement(Label, _extends({
    required: required,
    disabled: disabled,
    s: {
      mb: 2
    }
  }, labelProps), label), /*#__PURE__*/React.createElement(StyledDropdown, {
    $hasError: dateError,
    direction: direction,
    customHandler: customHandler
  }, _ref15 => {
    let {
      isOpen,
      onItemClick
    } = _ref15;
    return /*#__PURE__*/React.createElement(StyledDropdownList, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(StyledCalendar, {
      value: searchValue,
      onChange: v => {
        setDateError(false);
        onItemClick();
        handleChange(v);
      }
    }));
  }), error && /*#__PURE__*/React.createElement(Error, null, error));
};
export default InputDate;